import React, { useEffect, useMemo, useState } from "react";
import { Card, Spinner, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBullseye, faFireFlameCurved } from "@fortawesome/free-solid-svg-icons";
import api from "../../api/API";
import { getTarefasDoDia, toggleTarefaDia } from "../../api/diasApiTemp";
import PlanejamentoDiaModal from "../../pages/PlanejamentoDiaModal";
import "./DailyFocusCard.css"; // se você já tiver, mantém; se não tiver, pode remover

const DailyFocusCard = ({
  dia,
  selectedDiaTarefaId,
  setSelectedDiaTarefaId,
  onAbrirPlanejamento
}) => {
  const [tarefas, setTarefas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [score, setScore] = useState(0);
  const [showModal, setShowModal] = useState(false);

  const diaId = dia?.id;

  // Sempre que trocar o dia, recarrega as tarefas.
  useEffect(() => {
    if (!diaId) {
      setTarefas([]);
      setScore(0);
      return;
    }

    carregarTudo(diaId);
    // eslint-disable-next-line
  }, [diaId]);

  async function carregarTudo(idDia) {
    try {
      setLoading(true);

      const [resTarefas, resScore] = await Promise.all([
        getTarefasDoDia(idDia),
        api.get(`/dias/${idDia}/score`).catch(() => ({ data: { score: 0 } }))
      ]);

      setTarefas(resTarefas.data || []);
      setScore(resScore.data?.score ?? 0);
    } catch (err) {
      console.error("Erro ao carregar tarefas do dia:", err);
      setTarefas([]);
      setScore(0);
    } finally {
      setLoading(false);
    }
  }

  async function handleToggle(idDiaTarefa) {
    try {
      await toggleTarefaDia(idDiaTarefa);
      await carregarTudo(diaId);
    } catch (err) {
      console.error("Erro ao alternar tarefa:", err);
    }
  }

  function abrirPlanejamentoLocal() {
    // se o pai passou callback (ex: abre modal global), usa ele
    if (onAbrirPlanejamento) return onAbrirPlanejamento();
    // senão, abre aqui mesmo
    setShowModal(true);
  }

  const prioridades = useMemo(
    () => (tarefas || []).filter((t) => Number(t.tipo) !== 3),
    [tarefas]
  );

  const secundarias = useMemo(
    () => (tarefas || []).filter((t) => Number(t.tipo) === 3),
    [tarefas]
  );

  const concluidas = useMemo(
    () => prioridades.filter((t) => t.concluido).length,
    [prioridades]
  );

  if (!diaId) {
    return (
      <Card className="p-4 text-center">
        <div className="text-muted">Carregando dia...</div>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card className="p-4 text-center">
        <Spinner />
      </Card>
    );
  }

  return (
    <>
      <Card className="daily-focus-card p-4">

        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 className="mb-1">
              <FontAwesomeIcon icon={faBullseye} className="daily-focus-title-icon" /> Foco do dia
            </h5>
            <small className="text-muted">
              {concluidas}/{prioridades.length} concluídas
            </small>
          </div>

          <div className="score-badge">
            <FontAwesomeIcon icon={faFireFlameCurved} /> Score do Dia: {score}/3
          </div>

          <Button
            variant="light"
            size="sm"
            className="daily-focus-plan-btn"
            onClick={abrirPlanejamentoLocal}
          >
            Planejar
          </Button>
        </div>

        {/* Prioridades */}
        {prioridades.length === 0 ? (
          <div className="empty-focus">
            Nenhuma atividade definida para este dia
            <br />
            <Button
              variant="success"
              size="sm"
              className="mt-2"
              onClick={abrirPlanejamentoLocal}
            >
              Fazer planejamento
            </Button>
          </div>
        ) : (
          prioridades.map((t) => {
            const selected = Number(selectedDiaTarefaId) === Number(t.id);

            return (
              <div
                key={t.id}
                className={`focus-item ${t.concluido ? "done" : ""} ${selected ? "selected" : ""}`}
                onClick={() => setSelectedDiaTarefaId?.(t.id)}
                onDoubleClick={() => handleToggle(t.id)}
                style={{ cursor: "pointer" }}
                title="Clique para selecionar. Duplo clique para marcar/desmarcar."
              >
                <input
                  type="checkbox"
                  checked={!!t.concluido}
                  readOnly
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggle(t.id);
                  }}
                />

                <div className="ms-2 w-100">
                  <small className="text-success">Atividade</small>
                  <div>{t.descricao}</div>
                </div>
              </div>
            );
          })
        )}

        {/* Secundárias */}
        {secundarias.length > 0 && (
          <>
            <hr />

            <h6 className="text-muted mb-2">Atividades secundárias</h6>

            {secundarias.map((t) => {
              const selected = Number(selectedDiaTarefaId) === Number(t.id);

              return (
                <div
                  key={t.id}
                  className={`secondary-item ${t.concluido ? "done" : ""} ${selected ? "selected" : ""}`}
                  onClick={() => setSelectedDiaTarefaId?.(t.id)}
                  onDoubleClick={() => handleToggle(t.id)}
                  style={{ cursor: "pointer" }}
                  title="Clique para selecionar. Duplo clique para marcar/desmarcar."
                >
                  <input
                    type="checkbox"
                    checked={!!t.concluido}
                    readOnly
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggle(t.id);
                    }}
                  />
                  <span className="ms-2">{t.descricao}</span>
                </div>
              );
            })}
          </>
        )}
      </Card>

      {/* Modal (se você quiser abrir local) */}
      <PlanejamentoDiaModal
        show={showModal}
        onHide={() => setShowModal(false)}
        dia={dia}
        onSaved={() => {
          setShowModal(false);
          carregarTudo(diaId);
        }}
      />
    </>
  );
};

export default DailyFocusCard;
